import { Badge, Group, Loader, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import {
  IconBuildingStore,
  IconPackage,
  IconReceipt2,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../auth/authStore';

interface ModulesResponse {
  modules: string[];
}

interface BranchDto {
  branchId: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  isActive: boolean;
}

interface ItemDto {
  itemId: string;
  sku: string;
  isActive: boolean;
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const modulesQuery = useQuery({
    queryKey: ['me', 'modules'],
    queryFn: async () => (await apiClient.get<ModulesResponse>('/api/me/modules')).data,
  });
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/api/branches')).data,
  });
  const itemsQuery = useQuery({
    queryKey: ['items'],
    queryFn: async () => (await apiClient.get<ItemDto[]>('/api/items')).data,
  });

  const displayName = user
    ? i18n.language === 'ar'
      ? user.fullNameAr
      : (user.fullNameEn ?? user.fullNameAr)
    : '';

  const activeBranches = branchesQuery.data?.filter((b) => b.isActive).length ?? 0;
  const totalItems = itemsQuery.data?.length ?? 0;
  const activeItems = itemsQuery.data?.filter((i) => i.isActive).length ?? 0;

  return (
    <Stack gap="xl">
      {/* Page header */}
      <div>
        <Text fz="sm" c="var(--wheels-ink-muted)" mb={4}>
          {t('dashboard.eyebrow')}
        </Text>
        <Title order={2} c="var(--wheels-ink-strong)">
          {t('dashboard.greeting', { name: displayName })}
        </Title>
      </div>

      {/* Stat strip */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          icon={<IconBuildingStore size={20} stroke={1.6} />}
          label={t('dashboard.stats.activeBranches')}
          value={activeBranches}
          loading={branchesQuery.isLoading}
        />
        <StatCard
          icon={<IconPackage size={20} stroke={1.6} />}
          label={t('dashboard.stats.activeItems')}
          value={activeItems}
          sub={t('dashboard.stats.activeItemsSub', { total: totalItems })}
          loading={itemsQuery.isLoading}
        />
        <StatCard
          icon={<IconShieldCheck size={20} stroke={1.6} />}
          label={t('dashboard.stats.licensedModules')}
          value={modulesQuery.data?.modules.length ?? 0}
          loading={modulesQuery.isLoading}
        />
        <StatCard
          icon={<IconReceipt2 size={20} stroke={1.6} />}
          label={t('dashboard.stats.todaysOrders')}
          value="—"
          sub={t('dashboard.stats.comingSoon')}
        />
      </SimpleGrid>

      {/* Licensed modules card */}
      <Paper p="lg">
        <Group justify="space-between" align="center" mb="md">
          <div>
            <Title order={4} c="var(--wheels-ink-strong)">
              {t('dashboard.modules.title')}
            </Title>
            <Text fz="sm" c="var(--wheels-ink-muted)">
              {t('dashboard.modules.subtitle')}
            </Text>
          </div>
        </Group>

        {modulesQuery.isLoading ? (
          <Loader size="sm" />
        ) : !modulesQuery.data || modulesQuery.data.modules.length === 0 ? (
          <Text c="var(--wheels-ink-muted)">{t('dashboard.modules.empty')}</Text>
        ) : (
          <Group gap="xs">
            {modulesQuery.data.modules.map((code) => (
              <Badge
                key={code}
                variant="light"
                size="lg"
                color="gray"
                radius="sm"
                tt="none"
                styles={{
                  root: {
                    backgroundColor: 'var(--wheels-bg)',
                    borderColor: 'var(--wheels-border)',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: 'var(--wheels-ink)',
                    fontWeight: 500,
                  },
                }}
              >
                {code}
              </Badge>
            ))}
          </Group>
        )}
      </Paper>
    </Stack>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  loading?: boolean;
}

function StatCard({ icon, label, value, sub, loading }: StatCardProps) {
  return (
    <Paper p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          <Text fz="xs" tt="uppercase" lts="0.04em" c="var(--wheels-ink-muted)" fw={500}>
            {label}
          </Text>
          <Text
            fz={28}
            fw={600}
            c="var(--wheels-ink-strong)"
            lh={1.1}
            className="font-numeric"
          >
            {loading ? '…' : value}
          </Text>
          {sub && (
            <Text fz="xs" c="var(--wheels-ink-muted)">
              {sub}
            </Text>
          )}
        </Stack>
        <div className="grid h-10 w-10 place-items-center rounded-md bg-[var(--wheels-bg)] text-[var(--wheels-ink-muted)]">
          {icon}
        </div>
      </Group>
    </Paper>
  );
}
